import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./LicensesCertifications.css";

const LicensesCertifications = () => {
  const [activeCategory, setActiveCategory] = useState("company");

  const certificationData = {
    company: {
      title: "Company Certifications",
      icon: "🏢",
      description:
        "Organizational certifications demonstrating our commitment to security excellence.",
      items: [
        {
          name: "ISO 27001:2013",
          issuer: "International Organization for Standardization",
          type: "Information Security Management",
          validUntil: "December 2025",
          description:
            "Comprehensive information security management system certification.",
          scope: "Information security management for cybersecurity services",
        },
        {
          name: "SOC 2 Type II",
          issuer: "American Institute of CPAs",
          type: "Security & Availability",
          validUntil: "June 2025",
          description:
            "Independent audit of security, availability, and confidentiality controls.",
          scope: "Security monitoring and advisory services",
        },
        {
          name: "GDPR Compliance Certificate",
          issuer: "European Data Protection Authority",
          type: "Data Protection",
          validUntil: "Ongoing",
          description:
            "Certification of GDPR compliance for European data subjects.",
          scope: "Personal data processing and protection",
        },
        {
          name: "NIST Cybersecurity Framework",
          issuer: "National Institute of Standards and Technology",
          type: "Cybersecurity Framework",
          validUntil: "Ongoing",
          description: "Adherence to NIST cybersecurity framework standards.",
          scope: "Comprehensive cybersecurity risk management",
        },
      ],
    },
    technical: {
      title: "Technical Team Certifications",
      icon: "⚙️",
      description:
        "Professional certifications held by our cybersecurity experts.",
      items: [
        {
          name:
            "CISSP (Certified Information Systems Security Professional)",
          issuer: "(ISC)²",
          type: "Information Security",
          count: "15 certified professionals",
          description:
            "Advanced information security management and architecture.",
          domains:
            "Security & Risk Management, Asset Security, Security Architecture",
        },
        {
          name: "OSCP (Offensive Security Certified Professional)",
          issuer: "Offensive Security",
          type: "Penetration Testing",
          count: "8 certified professionals",
          description: "Hands-on penetration testing and ethical hacking.",
          domains:
            "External penetration testing, exploit development, post-exploitation",
        },
        {
          name: "GCIH (GIAC Certified Incident Handler)",
          issuer: "SANS Institute",
          type: "Incident Response",
          count: "12 certified professionals",
          description:
            "Incident detection, response, and digital forensics.",
          domains: "Incident response, malware analysis, forensic investigation",
        },
      ],
    },
  };

  const categories = Object.entries(certificationData);

  return (
    <div className="row clearfix">
      <div className="col-sm-2" />
      <div className="col-sm-9" style={{ padding: 0 }}>
        <div className="container-fluid">
          {/* Page header */}
          <div className="block-header">
            <div className="row clearfix">
              <div className="col-md-6 col-sm-12">
                <h2>Licenses & Certifications</h2>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Home</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Licenses & Certifications
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>

          {/* Intro */}
          <section className="lc-section">
            <div className="card lc-card">
              <div className="header">
                <h2>Professional Excellence & Compliance</h2>
                <p className="text-muted">
                  iOdin maintains the highest standards of professional certification and
                  regulatory compliance. Our team holds industry-leading certifications and our
                  organization adheres to international security and compliance frameworks.
                </p>
              </div>
            </div>
          </section>

          {/* Categories + Summary (left) | Grid + Audit (right) */}
          <section className="lc-section">
            <div className="row">
              {/* LEFT COLUMN */}
              <div className="col-xl-3 col-lg-4 col-md-12">
                {/* Categories */}
                <div className="card lc-card sticky-desktop">
                  <div className="header">
                    <h2>Certification Categories</h2>
                  </div>
                  <div className="body">
                    <ul className="lc-pills" role="tablist" aria-label="Certification categories">
                      {categories.map(([key, category]) => {
                        const selected = activeCategory === key;
                        return (
                          <li key={key}>
                            <button
                              type="button"
                              className={`lc-pill w-100 ${selected ? "active" : ""}`}
                              onClick={() => setActiveCategory(key)}
                              role="tab"
                              aria-selected={selected}
                              aria-controls={`panel-${key}`}
                              id={`tab-${key}`}
                            >
                              <span className="lc-pill-icon">{category.icon}</span>
                              <span className="lc-pill-label">{category.title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                {/* Certification Summary (aligned under categories) */}
                <div className="card lc-card mt-3">
                  <div className="header">
                    <h2>Certification Summary</h2>
                  </div>
                  <div className="body lc-summary">
                    <div className="lc-stat">
                      <h3 className="text-primary">50+</h3>
                      <p className="text-muted">Professional Certifications</p>
                    </div>
                    <div className="lc-stat">
                      <h3 className="text-success">15+</h3>
                      <p className="text-muted">Industry Standards</p>
                    </div>
                    <div className="lc-stat">
                      <h3 className="text-info">100%</h3>
                      <p className="text-muted">Compliance Rate</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="col-xl-9 col-lg-8 col-md-12">
                {/* Grid */}
                <div
                  className="card lc-card"
                  role="tabpanel"
                  id={`panel-${activeCategory}`}
                  aria-labelledby={`tab-${activeCategory}`}
                >
                  <div className="header">
                    <h2 className="d-flex align-items-center">
                      <span className="mr-2">
                        {certificationData[activeCategory].icon}
                      </span>
                      {certificationData[activeCategory].title}
                    </h2>
                    <p className="text-muted">
                      {certificationData[activeCategory].description}
                    </p>
                  </div>

                  <div className="body">
                    <div className="lc-grid">
                      {certificationData[activeCategory].items.map((cert, i) => (
                        <article key={i} className="card lc-tile">
                          <div className="body lc-tile-body">
                            <div className="lc-header">
                              <h5 className="lc-name">{cert.name}</h5>
                              <span className="badge badge-primary lc-type-badge">
                                {cert.type}
                              </span>
                            </div>

                            <div className="lc-meta">
                              <p className="mb-2">
                                <strong>Issued by:</strong> {cert.issuer}
                              </p>
                              {cert.validUntil && (
                                <p className="mb-2">
                                  <strong>Valid Until:</strong>{" "}
                                  <span className="badge badge-success ml-1">
                                    {cert.validUntil}
                                  </span>
                                </p>
                              )}
                              {cert.count && (
                                <p className="mb-2">
                                  <strong>Team Members:</strong>{" "}
                                  <span className="badge badge-info ml-1">
                                    {cert.count}
                                  </span>
                                </p>
                              )}
                            </div>

                            <p className="text-muted mb-3">{cert.description}</p>

                            <div className="lc-scope mt-auto">
                              <strong>
                                {cert.scope ? "Scope:" : cert.domains ? "Key Domains:" : ""}
                              </strong>
                              <p className="small text-secondary mb-0">
                                {cert.scope || cert.domains}
                              </p>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Audit under grid */}
                <div className="card lc-card mt-3">
                  <div className="header">
                    <h2>Audit & Verification</h2>
                  </div>
                  <div className="body">
                    <p className="mb-3">
                      All certifications are independently verified and audited. Certification
                      details are available upon request for compliance purposes.
                    </p>
                    <Link to="/contact" className="btn btn-outline-primary btn-round">
                      Request Verification
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default LicensesCertifications;